<template>
  <div class="faraday-cage-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$E_内部 = 0 (静电平衡)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <!-- The Faraday Cage (Metal Cavity) -->
          <rect x="140" y="50" width="120" height="120" fill="#1e293b" stroke="#64748b" stroke-width="4" rx="12" />
          
          <!-- Grid structure -->
          <g stroke="#334155" stroke-width="1.5">
            <line v-for="n in 5" :key="'h'+n" x1="140" :y1="50 + n*20" x2="260" :y2="50 + n*20" />
             <line v-for="n in 5" :key="'v'+n" :x1="140 + n*20" y1="50" :x2="140 + n*20" y2="170" />
          </g>

          <g v-if="powerOn">
            <!-- External Electric Field Lines terminating CORRECTLY -->
            <!-- Left side (Lines entering into negative charges) -->
            <g opacity="0.6">
               <path v-for="y in 6" :key="'efl'+y" :d="`M 10,${20 + y*25} L 138,${20 + y*25}`" stroke="#06b6d4" stroke-width="2" marker-end="url(#arr-e)" />
            </g>
            <!-- Right side (Lines leaving from positive charges) -->
            <g opacity="0.6">
               <path v-for="y in 6" :key="'efr'+y" :d="`M 262,${20 + y*25} L 390,${20 + y*25}`" stroke="#06b6d4" stroke-width="2" marker-end="url(#arr-e)" />
            </g>

            <!-- Induced Surface Charges blocking the field -->
            <circle v-for="y in 6" :key="'cn'+y" cx="140" :cy="20 + y*25" r="4" fill="#06b6d4" />
            <text v-for="y in 6" :key="'cnt'+y" x="131" :y="24 + y*25" fill="#06b6d4" font-size="12" font-weight="bold">-</text>

            <circle v-for="y in 6" :key="'cp'+y" cx="260" :cy="20 + y*25" r="4" fill="#ef4444" />
            <text v-for="y in 6" :key="'cpt'+y" x="265" :y="24 + y*25" fill="#ef4444" font-size="12" font-weight="bold">+</text>
          </g>

          <!-- Sensitive Meter Inside -->
          <g transform="translate(180, 90)">
            <rect x="0" y="0" width="40" height="40" rx="4" fill="#0f172a" stroke="#475569" stroke-width="2" />
            <circle cx="20" cy="20" r="16" fill="#1e293b" />
            <line x1="20" y1="20" :x2="20 + needleAngle.x" :y2="20 + needleAngle.y" stroke="#ef4444" stroke-width="2" stroke-linecap="round" />
            <circle cx="20" cy="20" r="3" fill="#cbd5e1" />
            <text x="20" y="52" text-anchor="middle" fill="#94a3b8" font-size="9">内部场强</text>
          </g>
          
          <defs>
            <marker id="arr-e" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
               <polygon points="0 0, 6 2, 0 4" fill="#06b6d4" />
            </marker>
          </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>外部场强 E_ext</label>
              <span class="val">{{ powerOn ? fieldStrength : 0 }} <small>kV/m</small></span>
           </div>
           <div class="hud-item orange">
              <label>内部仪表读数</label>
              <span class="val">0.00 <small>kV/m</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">外部电场强度</label>
                    <el-slider v-model="fieldStrength" :min="10" :max="100" size="small" :disabled="!powerOn"></el-slider>
                 </div>
                 <div>
                    <el-button :type="powerOn ? 'danger' : 'success'" size="small" plain @click="powerOn = !powerOn">
                      {{ powerOn ? '关闭外部场源' : '开启外部场源' }}
                    </el-button>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>修正了此前电场线穿透法拉第笼的视觉错误！在静电平衡状态下，外部电场会驱动金属网壳上的<strong>自由电子重新分布</strong>（左负右正）。表面感应电荷在笼内产生的附加电场，与其外部电场发生干涉并<strong>完全抵消</strong>，使得笼体内部合场强恒为零。电力线在外表面发生垂直终止与重生，完美保护内部精密仪器不受外界高压电场干扰。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FaradayCageViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      powerOn: true,
      fieldStrength: 50,
      time: 0,
      timer: null
    }
  },
  computed: {
    needleAngle() {
       // internal E is zero so needle stays steady at 0 (straight up)
       // Add a very tiny random jitter for realism 
       let jitter = Math.sin(this.time * 5) * 1.5;
       let angle = -Math.PI / 2 + (jitter * Math.PI / 180);
       return {
          x: Math.cos(angle) * 12,
          y: Math.sin(angle) * 12
       }
    }
  },
  methods: {
    animate() {
       this.time += 0.05;
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if (this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.faraday-cage-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
